import { useContext, useEffect, useRef } from 'react';

import { APPROVALS_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';

import { isApprovalsDisabledError } from '../approvalsDisabledError.ts';
import { isExpectedApprovalsDataError } from '../ApprovalsDataErrorDisplay.tsx';

const APPROVALS_LOAD_TIMEOUT_MS = 10_000;

type UseApprovalsLoadExperienceProps = {
	contentId: string;
	contentStatus: string;
	/** False when the approvals query is skipped; the experience must not start at all. */
	isQueryEnabled: boolean;
	isLoading: boolean;
	hasData: boolean;
	/** The query's Apollo/Relay interop error normalises to a native Error subclass. */
	error: Error | undefined;
};

/**
 * Tracks `approvals/load` — did the approval data behind the banner, the pane and the
 * activity surfaces load for this page?
 *
 * The gate is read at the start boundary only: once an attempt has started it is always
 * terminated, so a gate value that changes mid-session cannot leave an orphan start to
 * time out and be counted as a failure.
 */
export const useApprovalsLoadExperience = ({
	contentId,
	contentStatus,
	isQueryEnabled,
	isLoading,
	hasData,
	error,
}: UseApprovalsLoadExperienceProps): void => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	// One attempt per content/mode pair: the provider stays mounted across client-side
	// navigation and briefly serves the previous page's data.
	const attemptKey = `${contentId}:${contentStatus}`;
	const attemptRef = useRef<{ key: string; isSettled: boolean } | null>(null);

	useEffect(() => {
		if (!isQueryEnabled || attemptRef.current?.key === attemptKey) {
			return;
		}

		if (!fg('confluence_approvals_fe_experience_tracking')) {
			attemptRef.current = { key: attemptKey, isSettled: true };
			return;
		}

		attemptRef.current = { key: attemptKey, isSettled: false };
		experienceTracker.start({
			name: APPROVALS_LOAD_EXPERIENCE,
			timeout: APPROVALS_LOAD_TIMEOUT_MS,
			attributes: { contentStatus },
		});
	}, [attemptKey, contentStatus, isQueryEnabled, experienceTracker]);

	useEffect(() => {
		const attempt = attemptRef.current;

		if (!attempt || attempt.key !== attemptKey || attempt.isSettled) {
			return;
		}

		// errorPolicy: 'all' can return data and an error together; an error always wins.
		if (error) {
			attempt.isSettled = true;

			const abortReason = isApprovalsDisabledError(error)
				? 'Approvals disabled'
				: isExpectedApprovalsDataError(error)
					? 'Expected data error'
					: undefined;

			if (abortReason) {
				experienceTracker.abort({
					name: APPROVALS_LOAD_EXPERIENCE,
					reason: abortReason,
					checkForTimeout: false,
					attributes: { contentStatus },
				});
				return;
			}

			// ApprovalsDataErrorDisplay already reports these through ErrorDisplay.
			experienceTracker.stopOnError({
				name: APPROVALS_LOAD_EXPERIENCE,
				error,
				attributes: { contentStatus },
			});
			return;
		}

		if (!isLoading && hasData) {
			attempt.isSettled = true;
			experienceTracker.succeed({
				name: APPROVALS_LOAD_EXPERIENCE,
				attributes: { contentStatus },
			});
		}
	}, [attemptKey, contentStatus, error, hasData, isLoading, experienceTracker]);
};
